import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import { getMyCourses } from '../../../services/userService';
import { encodeData } from '../../../utils/encodeAndEncode';
import { GET } from '../../../services/apiController';
import CourseCard from './CourseCard';

const SkeletonCard = () => (
  <div className="bg-[var(--bg-primary)] rounded-2xl overflow-hidden border border-[var(--border-color)] animate-pulse">
    <div className="aspect-[16/10] bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-6 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
      <div className="flex gap-4 mt-4">
        <div className="h-4 bg-gray-200 rounded w-20" />
        <div className="h-4 bg-gray-200 rounded w-20" />
      </div>
    </div>
  </div>
);

function MyCourseTab({ searchTerm: externalSearchTerm, setIsSearchLoading: externalSetIsSearchLoading }) {
  const navigate = useNavigate();
  const observer = useRef();
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [clickedCourseId, setClickedCourseId] = useState(null);
  const [sortBy, setSortBy] = useState('default');

  // Use external search term if provided
  useEffect(() => {
    if (externalSearchTerm !== undefined) {
      setSearchTerm(externalSearchTerm);
    }
  }, [externalSearchTerm]);

  const fetchMyCourses = async (pageNumber = 1, search = '', reset = false) => {
    try {
      setIsLoading(true);
      if (externalSetIsSearchLoading) externalSetIsSearchLoading(true);
      const response = await getMyCourses({ page: pageNumber, limit: 10, search });

      if (response.success && response.data?.courses?.length > 0) {
        console.log('🔍 Course data from API:', response.data.courses[0]); // Debug log
        console.log('🔍 Metadata from API:', response.data.metadata); // Debug log
        const mapped = response.data.courses.map((course, index) => ({
          id: course.course_id,
          title: course.course_name,
          description: course.course_desc,
          image: course.banner_image,
          course_url: course.course_url,
          modules: response.data.metadata[index]?.total_modules ?? 0,
          enrolled: response.data.metadata[index]?.totalUsers ?? 0,
          rating: course.total_rating ?? 0,
          level: course.levels || 'N/A',
          course_expiry: course.course_expiry,
          isExpired: course.course_expiry ? new Date(course.course_expiry) < new Date() : false,
          is_mandatory: course.is_mandatory,
          is_completed: Number(course.is_completed) === 1,
          instructor_name: course.trainer_name || course.instructor_name || null,
          progress: response.data.metadata[index]?.progress ?? 0,
        }));
        console.log('🔍 Mapped course:', mapped[0]); // Debug log

        const sortCourses = (list) => {
          return [...list].sort((a, b) => {
            if (sortBy === 'completed') {
              const aCompleted = a.is_completed ? 1 : 0;
              const bCompleted = b.is_completed ? 1 : 0;
              if (aCompleted !== bCompleted) return bCompleted - aCompleted;
            }
            if (sortBy === 'mandatory') {
              const aMandatory = Number(a.is_mandatory) === 1 ? 1 : 0;
              const bMandatory = Number(b.is_mandatory) === 1 ? 1 : 0;
              if (aMandatory !== bMandatory) return bMandatory - aMandatory;
            }

            const aMandatory = Number(a.is_mandatory) === 1 ? 1 : 0;
            const bMandatory = Number(b.is_mandatory) === 1 ? 1 : 0;
            if (aMandatory !== bMandatory) return bMandatory - aMandatory;

            const aExpired = a.isExpired ? 1 : 0;
            const bExpired = b.isExpired ? 1 : 0;
            if (aExpired !== bExpired) return aExpired - bExpired;

            const aCompleted = a.is_completed ? 1 : 0;
            const bCompleted = b.is_completed ? 1 : 0;
            if (aCompleted !== bCompleted) return aCompleted - bCompleted;

            if (sortBy === 'progress-high') return (b.progress || 0) - (a.progress || 0);
            if (sortBy === 'progress-low') return (a.progress || 0) - (b.progress || 0);
            if (sortBy === 'title-az') return String(a.title || '').localeCompare(String(b.title || ''));
            if (sortBy === 'title-za') return String(b.title || '').localeCompare(String(a.title || ''));

            return String(a.title || '').localeCompare(String(b.title || ''));
          });
        };

        setCourses((prev) => {
          const next = reset ? mapped : [...prev, ...mapped];
          return sortCourses(next);
        });
        setPage(pageNumber);
        setHasMore(pageNumber < response.data.totalPages);
      } else {
        if (reset) setCourses([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setHasMore(false);
      if (reset) setCourses([]);
    } finally {
      setIsLoading(false);
      if (externalSetIsSearchLoading) externalSetIsSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchMyCourses(1, searchTerm, true);
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (courses.length > 0) {
      setCourses(prev => {
        const sorted = [...prev].sort((a, b) => {
          if (sortBy === 'completed') {
            const aCompleted = a.is_completed ? 1 : 0;
            const bCompleted = b.is_completed ? 1 : 0;
            if (aCompleted !== bCompleted) return bCompleted - aCompleted;
          }
          if (sortBy === 'mandatory') {
            const aMandatory = Number(a.is_mandatory) === 1 ? 1 : 0;
            const bMandatory = Number(b.is_mandatory) === 1 ? 1 : 0;
            if (aMandatory !== bMandatory) return bMandatory - aMandatory;
          }

          const aMandatory = Number(a.is_mandatory) === 1 ? 1 : 0;
          const bMandatory = Number(b.is_mandatory) === 1 ? 1 : 0;
          if (aMandatory !== bMandatory) return bMandatory - aMandatory;

          const aExpired = a.isExpired ? 1 : 0;
          const bExpired = b.isExpired ? 1 : 0;
          if (aExpired !== bExpired) return aExpired - bExpired;

          const aCompleted = a.is_completed ? 1 : 0;
          const bCompleted = b.is_completed ? 1 : 0;
          if (aCompleted !== bCompleted) return aCompleted - bCompleted;

          if (sortBy === 'progress-high') return (b.progress || 0) - (a.progress || 0);
          if (sortBy === 'progress-low') return (a.progress || 0) - (b.progress || 0);
          if (sortBy === 'title-az') return String(a.title || '').localeCompare(String(b.title || ''));
          if (sortBy === 'title-za') return String(b.title || '').localeCompare(String(a.title || ''));

          return String(a.title || '').localeCompare(String(b.title || ''));
        });
        return sorted;
      });
    }
  }, [sortBy]);

  useEffect(() => {
    if (page > 1) fetchMyCourses(page, searchTerm, false);
  }, [page]);

  const lastCourseRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) setPage((prev) => prev + 1);
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const handleCourseClick = async (course) => {
    setClickedCourseId(course.id);
    
    // If course is expired, renew it first
    if (course.isExpired) {
      try {
        const response = await GET(`/org/add_to_mycourse?course_id=${course.id}`);
        
        if (response.success) {
          // Refresh the course list to update expiry date
          await fetchMyCourses(1, searchTerm, true);
          setClickedCourseId(null);
          return;
        }
      } catch (error) {
        // Payment required - redirect to course details for payment
        if (error.response?.status === 402) {
          const encoded = encodeData({ id: course.id });
          navigate(`/user/courses/details/${encodeURIComponent(encoded)}`);
          return;
        }
        console.error('Error renewing course:', error);
      }
      setClickedCourseId(null);
      return;
    }
    
    // Navigate to course watch page
    const encoded = encodeData({ id: course.id });
    setTimeout(() => {
      navigate(`/user/courses/WatchCourse/${encodeURIComponent(encoded)}`);
    }, 300);
  };

  return (
    <div className="course-tab-content">
      {/* Sort Dropdown */}
      {courses.length > 0 && (
        <div className="flex justify-end mb-4">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] cursor-pointer"
          >
            <option value="default">Sort by: Default</option>
            <option value="completed">Completed</option>
            <option value="mandatory">Mandatory</option>
            <option value="progress-high">Progress: High to Low</option>
            <option value="progress-low">Progress: Low to High</option>
            <option value="title-az">Title: A to Z</option>
            <option value="title-za">Title: Z to A</option>
          </select>
        </div>
      )}

      {courses.length === 0 && !isLoading && (
        <div className="flex justify-center items-center w-full min-h-[340px] rounded-2xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <NoData message="You haven't enrolled in any courses yet. Go to Explore Courses to get started." />
        </div>
      )}

      {isLoading && courses.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {courses.map((course, index) => (
            <CourseCard
              key={course.id}
              course={course}
              mode="my-course"
              onAction={handleCourseClick}
              isActionLoading={clickedCourseId === course.id}
              lastRef={index === courses.length - 1 ? lastCourseRef : null}
            />
          ))}
        </div>
      )}

      {isLoading && courses.length > 0 && (
        <div className="flex justify-center py-6">
          <span className="w-8 h-8 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin" role="status" />
        </div>
      )}
    </div>
  );
}

export default MyCourseTab;
